<template>
  <div v-show="isShowUpdate" id="punch-card-campaign-update-wrapper">
    <div id="punch-card-campaign-update-container" :class="{ 'shown': isSlide }">
      <img src="@/assets/images/close-dark-gray.svg" alt="close-update" class="cursor-pointer"
        @click="handleSidebarClosing">
      <b-container fluid class="update-form-container h-100">
        <div class="position-sticky bg-white update-punch-card-sidebar-header">
          <div class="d-flex align-items-start justify-content-between">
            <div class="flex-grow-1">
              <h3>{{ campaignData.details.name }}</h3>
              <b-dropdown id="dropdown-punch-card-campaign-update-status" class="flex-grow-1"
                toggle-class="text-left bg-transparent border text-dark shadow-none" menu-class="w-100 py-0" no-caret>
                <template slot="button-content">
                  <div
                    :class="['d-flex align-items-center punch-card-status', `-${campaignData.status ? campaignData.status.toLowerCase() : ''}`, { 'unselected': !campaignData.status }]">
                    <div v-if="campaignData.status"
                      :class="`status-dot -${campaignData.status ? campaignData.status.toLowerCase() : ''} mr-2`"></div>
                    {{
                      selectedStatusText }}
                  </div>
                </template>
                <b-dropdown-item v-for="(status, key) in statusList" :key="`status-option-${key}`"
                  :id="`option-punch-card-campaign-update-status-${status.value.toLowerCase()}`"
                  :class="['punch-card-status', `-${status.value.toLowerCase()}`, { 'd-inline-flex align-items-center': status.enabled, 'd-none': !status.enabled }]"
                  @click="campaignData.status = status.value">
                  <div v-if="status.value.toLowerCase() !== 'all'"
                    :class="['status-dot mr-2', `-${status.value.toLowerCase()}`]">
                  </div>
                  {{ status.label }}
                </b-dropdown-item>
              </b-dropdown>
            </div>
            <PunchCardActionButtons class="flex-shrink-0" source="update" />
          </div>
          <div class="punch-card-update-tabs">
            <b-button v-for="(component, index) in updateComponents"
              :key="`button-update-component-${component.template.toLowerCase()}-${index}`"
              :id="`button-update-component-${component.template.toLowerCase()}-${index}`"
              @click="setCurrentComponent(component.template)"
              :class="['shadow-none', { active: currentComponent === component.template }]">{{ component.label
              }}</b-button>
          </div>
        </div>
        <Details ref="updateDetails" v-show="currentComponent === 'Details'" :source="`update-${slideLeftKey}`" />
        <Execution ref="updateExecution" v-show="currentComponent === 'Execution'" :source="`update-${slideLeftKey}`" />
        <Design ref="updateDesign" v-show="currentComponent === 'Design'" :source="`update-${slideLeftKey}`" />
      </b-container>
      <div class="update-punch-card-sidebar-footer text-center">
        <b-button variant="primary" class="btn-pill" id="button-punch-card-campaign-save-changes"
          @click="handleSaveChanges" :disabled="!isTouched || loading || uploadingImg || isSaving">
          Save Changes
          <b-spinner small class="ml-1" v-if="loading || uploadingImg || isSaving"></b-spinner>
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import Details from "@/components/punch-card/create-campaign/Details.vue";
import Execution from "@/components/punch-card/create-campaign/Execution.vue";
import Design from "@/components/punch-card/create-campaign/Design.vue";
import PunchCardActionButtons from "./PunchCardActionButtons.vue";
import { mapFields } from "vuex-map-fields";
import { mapGetters, mapActions } from "vuex";
import _ from "lodash";
import { existingPresets, isValidUrl, stampsPreset } from "./punch.card.utilities";

export default {
  props: {
    isShowUpdate: {
      type: Boolean,
      default: false
    }
  },
  components: { Details, Execution, Design, PunchCardActionButtons },
  computed: {
    ...mapFields("punchCard", ["campaignData", "campaignDataUntouched", "errors", "modalOpeningKey", "isSaving"]),
    ...mapGetters("punchCard", ["campaignDetails", "loading", "uploadingImg"]),
    selectedStatusText: {
      get() {
        if (this.campaignData.status)
          return this.statusList.find((status) => status.value === this.campaignData.status).label;

        return this.statusList[0].label;
      },
      set(text) {
        return text;
      }
    },
    formattedCampaignData: {
      get() {
        const { details, execution, design } = this.campaignData;

        return {
          status: this.campaignData.status,
          ...details,
          ...execution,
          ...design
        };
      },
      set(campaign) {
        return campaign;
      }
    },
    isTouched: {
      get() {
        return JSON.stringify(this.campaignDataUntouched) !== JSON.stringify(this.formattedCampaignData);
      },
      set(status) {
        return status;
      }
    }
  },
  data() {
    return {
      isSlide: false,
      currentComponent: "Details",
      updateComponents: [
        { template: "Details", label: "Stamp Details" },
        { template: "Execution", label: "Execution Details" },
        { template: "Design", label: "Design" }
      ],
      statusList: [
        { value: "ACTIVE", label: "Active", enabled: true },
        { value: "INACTIVE", label: "Inactive", enabled: false },
        { value: "DISABLED", label: "Disabled", enabled: true }
      ],
      slideLeftKey: 0
    }
  },
  mounted() {
    this.$root.$on("validateUpdateCampaign", () => this.validateForms());
    this.$root.$on("discardCampaignChanges", () => this.slideRight({ isDiscard: true }));
    this.$root.$on("disablePunchCardCampaign", () => this.submitCampaignUpdates());
  },
  beforeDestroy() {
    this.$root.$off("validateUpdateCampaign");
    this.$root.$off("discardCampaignChanges");
    this.$root.$on("disablePunchCardCampaign");
  },
  methods: {
    ...mapActions({
      "uploadImage": "punchCard/punchCardUploadImage"
    }),
    handleSidebarClosing() {
      if (this.isTouched)
        this.$root.$emit("bv::show::modal", "punch-card-campaign-leave-without-saving-updates-modal");
      else
        this.slideRight({ isDiscard: false });
    },
    slideRight({ isDiscard = false, isReloadList = false }) {
      if (isDiscard) this.closeLeaveWithoutSavingModal();

      this.isSlide = false;
      this.currentComponent = "Details";
      if (isReloadList) this.$root.$emit("doSeachPunchCardCampaign");

      setTimeout(() => {
        this.$emit("hideCampaignUpdate");
        this.$store.commit("punchCard/INIT_CAMPAIGN_DATA");
        this.campaignDataUntouched = null;
        this.errors = null;
      }, 300);
    },
    setCurrentComponent(template) {
      this.currentComponent = template;
      this.$nextTick(() => this.$root.$emit("evaluateDesignPreviewPosition"));
    },
    formatCampaign() {
      this.campaignData.status = this.campaignDetails.status;
      this.formatCampaignDetails();
      this.formatCampaignExecution();
      this.formatCampaignDesign();

      this.$nextTick(() => this.setCampaignDataUntouched());
    },
    handleSaveChanges() {
      this.validateForms();
    },
    closeLeaveWithoutSavingModal() {
      this.$root.$emit("bv::hide::modal", "punch-card-campaign-leave-without-saving-updates-modal");
    },
    validateForms() {
      const { updateDetails, updateExecution, updateDesign } = this.$refs;
      updateDetails.$v.$touch();
      updateExecution.$v.$touch();

      const designSubComponents = ["Stamps", "Rewards", "Customize"]
        .map((componentTag) =>
          updateDesign.$children.find(({ $options }) => $options._componentTag === componentTag)
        );

      designSubComponents.forEach((subComponent) => {
        subComponent.$v.$touch();
      });

      const subComponentsInvalidations = designSubComponents.map((subComponent) => subComponent.$v.$dirty && !subComponent.$v.$invalid);
      const designHasNoErrors = !subComponentsInvalidations.includes(false);

      this.$nextTick(() => {
        if (
          updateDetails.$v.$dirty && !updateDetails.$v.$invalid &&
          updateExecution.$v.$dirty && !updateExecution.$v.$invalid &&
          designHasNoErrors
        ) {
          if (this.campaignDataUntouched.status == "ACTIVE" && this.campaignData.status === "DISABLED") {
            this.closeLeaveWithoutSavingModal();
            this.$root.$emit("bv::show::modal", "punch-card-confirm-update-status-modal");
          } else
            this.submitCampaignUpdates();
        } else {
          this.reopenTabForErrors(updateDetails, updateExecution, updateDesign, designSubComponents);
          this.closeLeaveWithoutSavingModal();
        }
      });
    },
    reopenTabForErrors(updateDetails, updateExecution) {
      if (updateDetails.$v.$invalid) this.currentComponent = "Details";
      else if (
        !updateDetails.$v.$invalid &&
        updateExecution.$v.$invalid
      ) this.currentComponent = "Execution";
      else if (
        !updateDetails.$v.$invalid &&
        !updateExecution.$v.$invalid
      ) this.currentComponent = "Design";
    },
    setCampaignDataUntouched() {
      this.campaignDataUntouched = _.cloneDeep({
        status: this.campaignData.status,
        ...this.campaignData.details,
        ...this.campaignData.execution,
        ...this.campaignData.design
      });
    },
    formatCampaignDetails() {
      _.keysIn(this.campaignData.details).forEach((key) => this.campaignData.details[key] = this.campaignDetails[key]);
    },
    formatCampaignExecution() {
      const [publishing_date, publishing_time] = this.campaignDetails.publishing_date && this.campaignDetails.publishing_date.split(" ") || [];

      _.keysIn(this.campaignData.execution).forEach((key) => {
        if (key === "is_open_duration") this.campaignData.execution[key] = !this.campaignDetails[key];
        else if ((key === "date_start" || key === "date_end") && this.campaignDetails[key]) {
          const [date] = this.campaignDetails[key].split(" ");
          this.$nextTick(() => this.campaignData.execution[key] = date);
        } else if (key === "publishing_date" && this.campaignDetails[key]) {
          const [hh, mm] = publishing_time.split(":");
          this.$nextTick(() => {
            this.campaignData.execution["publishing_time"] = { HH: "", mm: "" };
            this.campaignData.execution[key] = publishing_date;
            this.campaignData.execution.publishing_time.HH = hh;
            this.campaignData.execution.publishing_time.mm = mm;
          });
        } else if (key === "frequency") this.campaignData.execution[key] = Number(this.campaignDetails[key]);
        else if (key === "is_open_to_guest")
          this.campaignData.execution[key] = Boolean(this.campaignDetails[key]);
        else if (key === "publish_date_same_as_start_date")
          this.campaignData.execution[key] = Boolean(this.campaignDetails[key]);
        else this.campaignData.execution[key] = this.campaignDetails[key];
      });
    },
    formatCampaignDesign() {
      _.keysIn(this.campaignData.design).forEach((key) => {
        if (key === "customize") {
          _.keysIn(this.campaignData.design[key]).forEach((customizeKey) => {
            this.campaignData.design[key][customizeKey] = this.campaignDetails[customizeKey];
            if (customizeKey === "campaign_layout") {
              this.campaignData.design[key][customizeKey] = Number(this.campaignDetails[customizeKey]);
            }
          });
        } else if (key === "milestones") {
          this.campaignData.design[key] = this.campaignDetails[key].map((milestone) => ({
            ...milestone,
            is_main: !!milestone.reward_image,
            reward: {
              name: milestone.reward_name,
              uuid: milestone.reward_uuid
            },
            stamps_count: milestone.reward_image ? null : milestone.stamps_count
          }));
        } else if (key === "stamp_icon") {
          const [preset] = this.campaignDetails[key].split("preset-").pop().split("/");

          this.$nextTick(() => {
            this.campaignData.design[key] = this.campaignDetails[key];
            this.campaignData.design["stamp_preset"] = existingPresets.includes(preset) ? preset : null;
            this.campaignData.design["stamp_icon_old"] = this.campaignDetails[key];
            this.campaignData.design.stamp_icon = existingPresets.includes(preset) && preset !== "custom"
              ? stampsPreset.find(({ label }) => label === preset).icon
              : this.campaignDetails[key];
          });
        } else this.campaignData.design[key] = this.campaignDetails[key];
      });
    },
    async submitCampaignUpdates() {
      const {
        active,
        milestones,
        designVisibility,
        ...form
      } = this.campaignData;

      const {
        publishing_time,
        ...execution
      } = {
        ...form.execution,
        date_start: !form.execution.is_open_duration / 1 ? null : `${form.execution.date_start} 00:00:00`,
        date_end: !form.execution.is_open_duration / 1 ? null : `${form.execution.date_end} 23:59:59`,
        publish_date_same_as_start_date: form.execution.publish_date_same_as_start_date / 1,
        publishing_date: `${form.execution.publishing_date} ${form.execution.publishing_time.HH}:${form.execution.publishing_time.mm}:00`,
        is_open_duration: !form.execution.is_open_duration / 1,
        is_open_to_guest: form.execution.is_open_to_guest / 1
      };

      const extractStampIcon = () => {
        if (isValidUrl(form.design.stamp_icon))
          return form.design.stamp_icon;
        else if (!isValidUrl(form.design.stamp_icon) && !form.design.stamp_base64 && existingPresets.includes(form.design.stamp_preset))
          return form.design.stamp_icon_old;
        else return form.design.stamp_base64;
      };

      const {
        customize,
        stamp_base64,
        stamp_preset,
        stamp_icon_old,
        ...design
      } = {
        ...form.design,
        ...form.design.customize,
        stamp_icon: extractStampIcon(),
        number_of_stamps: Number(form.design.number_of_stamps)
      };

      const payload = {
        status: form.status,
        ...form.details,
        name: form.details.name.trim(),
        ...execution,
        ...design
      };

      payload.milestones = payload.milestones.map((milestone) => {
        const { is_main, reward, ...item } = {
          ...milestone,
          reward_name: milestone.reward.name,
          reward_uuid: milestone.reward.uuid,
          stamps_count: milestone.is_main ? Number(payload.number_of_stamps) : milestone.stamps_count
        }
        return item;
      });

      const imagesToUpload = {};
      const imagesToUploadArray = ["campaign_background_image", "stamp_icon", "reward_image", "image"];

      imagesToUploadArray.forEach((imgKey) => {
        if (imgKey === "reward_image") imagesToUpload[imgKey] = isValidUrl(payload.milestones[0].reward_image);
        else if (payload[imgKey]) imagesToUpload[imgKey] = isValidUrl(payload[imgKey]);
      });

      if (!_.values(imagesToUpload).includes(false)) {
        this.submitCampaign({
          uuid: this.campaignDetails.uuid,
          params: payload
        });
      } else {
        const imageUploadPromises = imagesToUploadArray.map(async (imgKey, imgIndex) => {
          this.isSaving = true;
          if (imgKey === "reward_image" && !imagesToUpload[imgKey]) {
            await this.uploadImage({
              file: payload.milestones[0].reward_image.split(";base64,").pop(),
              type: 3
            }).then((url) => payload.milestones[0].reward_image = url);
          } else if (imgKey === "stamp_icon" && !imagesToUpload[imgKey]) {
            await this.uploadImage({
              file: payload.stamp_icon.split(";base64,").pop(),
              type: 2,
              icon: form.design.stamp_preset
            }).then((url) => payload.stamp_icon = url);
          } else if (imagesToUpload[imgKey] !== undefined && !imagesToUpload[imgKey]) {
            await this.uploadImage({
              file: payload[imgKey].split(";base64,").pop(),
              type: imgIndex + 1
            }).then((url) => payload[imgKey] = url);
          }
        });

        Promise.all(imageUploadPromises).then((res) => {
          if (!this.errors) this.submitCampaign({
            uuid: this.campaignDetails.uuid,
            params: payload
          });
        });
      }
    },
    async submitCampaign({ uuid, params }) {
      await this.$store.dispatch("punchCard/updatePunchCardCampaign", { uuid, params });

      if (!this.errors) {
        this.closeLeaveWithoutSavingModal();
        this.$root.$emit("bv::hide::modal", "punch-card-confirm-update-status-modal");
        this.slideRight({ isReloadList: true });
      } else {
        const detailsErrors = ["name", "description", "terms_condition"];
        const executionErrors = ["date_start", "date_end", "publishing_date", "target_audience", "frequency"];
        const designErrors = ["number_of_stamps", "stamp_icon", "milestones", "image"];
        const errorKeys = Object.keys(this.errors);

        this.$nextTick(() => {
          if (detailsErrors.some((key) => errorKeys.includes(key))) this.currentComponent = "Details";
          else if (executionErrors.some((key) => errorKeys.includes(key))) this.currentComponent = "Execution";
          else if (designErrors.some((key) => errorKeys.includes(key))) this.currentComponent = "Design";
        });
      }
    }
  },
  watch: {
    isShowUpdate(value) {
      if (value) {
        setTimeout(() => {
          this.isSlide = value;
          this.slideLeftKey++;
          this.modalOpeningKey++;

          document.querySelector(".update-form-container").scrollTo({
            top: 0,
            behavior: "smooth"
          });
          this.formatCampaign();
        }, 100);
        document.body.style.overflowY = "hidden";
      } else {
        document.body.style.overflowY = null;
      }
    }
  }
}
</script>

<style lang="scss">
@import "~@/assets/scss/punch-card/_form.scss";

#dropdown-punch-card-campaign-update-status {
  .dropdown-toggle {
    height: 48px;
    background: transparent url("~@/assets/images/caret-down-2.svg") no-repeat right 12px center;
    padding-right: 2.75rem;
    border-radius: 10px;
    margin-bottom: 33px;
  }

  @import "~@/assets/scss/punch-card/_status.scss";

  .dropdown-item {
    &:active {
      background-color: transparent;
    }
  }
}
</style>

<style lang="scss" scoped>
#punch-card-campaign-update-wrapper {
  width: 100vw;
  height: 100vh;
  position: fixed;
  top: 0;
  right: 0;
  z-index: 9;
  background: rgba(0, 0, 0, 0.30);
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  0% {
    opacity: 0;
  }

  50% {
    opacity: 0.5;
  }

  100% {
    opacity: 1;
  }
}

#punch-card-campaign-update-container {
  border-top-left-radius: 20px;
  border-bottom-left-radius: 20px;
  background-color: #fff;
  width: 55vw;
  height: 100vh;
  position: fixed;
  left: 100vw;
  transition: all 0.3s ease-in;
  padding: 24px 26px;

  &.shown {
    left: 45vw;
  }

  .update-form-container {
    margin-top: 40px;
    padding-left: 82px;
    padding-right: 60px;
    max-height: 79vh;
    overflow-y: auto;

    .position-sticky {
      position: sticky;
      top: 0;
      z-index: 99;
    }
  }

  .update-punch-card-sidebar-header {
    h3 {
      font-family: MainFontBold, sans-serif;
      font-size: 24px;
      color: #49545C;
      margin-bottom: 26px;
    }

    .punch-card-update-tabs {
      border-bottom: solid 2px #C2C8CC;

      button {
        width: 164px;
        height: 42px;
        border-top-left-radius: 10px;
        border-top-right-radius: 10px;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        background-color: transparent;
        border: 0;
        color: rgba(73, 84, 92, 0.5);
        font-family: MainFontBold, sans-serif;

        &.active {
          background-color: #F9F9F9;
          color: #2F3941;
        }
      }
    }
  }

  .update-punch-card-sidebar-footer {
    padding-top: 25px;
    padding-bottom: 25px;
    position: sticky;
    bottom: 0;

    button {
      width: 200px;
      height: 44px;
    }
  }
}
</style>
